import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { playThruster } from "./sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_TYPES = [
  { id: "creator-pitch", label: "Creator Show Pitch" },
  { id: "advertise", label: "Advertise With Us" },
  { id: "partnership", label: "Partner With Us" },
  { id: "careers", label: "Careers" },
  { id: "press", label: "Media & Press" },
  { id: "general", label: "General Enquiry" },
];

const initialType = () => {
  const t = new URLSearchParams(window.location.search).get("enquiry");
  return ENQUIRY_TYPES.some((e) => e.id === t) ? t : "general";
};

export default function Contact() {
  const [count, setCount] = useState(null);
  const [wEmail, setWEmail] = useState("");
  const [wLoading, setWLoading] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", enquiry_type: initialType(), message: "" });
  const [sending, setSending] = useState(false);
  const rocketRef = useRef(null);
  const rocketInView = useInView(rocketRef, { once: true, margin: "-40px" });

  useEffect(() => {
    if (!rocketInView) return;
    const t = setTimeout(() => playThruster(0.5), 1000);
    return () => clearTimeout(t);
  }, [rocketInView]);

  const refreshCount = async () => {
    try {
      const { data } = await axios.get(`${API}/waitlist/count`);
      setCount(data.count);
    } catch {
      setCount(null);
    }
  };

  useEffect(() => {
    refreshCount();
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setWLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email: wEmail, source: "contact" });
      toast.success(data.already_joined ? "You're already on the signal." : "Locked in. See you at launch.");
      setWEmail("");
      refreshCount();
      window.dispatchEvent(new Event("lp:waitlist-joined"));
    } catch {
      toast.error("Signal interrupted — check that email and retry.");
    } finally {
      setWLoading(false);
    }
  };

  const sendEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Transmission received. Your Channel Manager will respond shortly.");
      setForm({ name: "", email: "", enquiry_type: "general", message: "" });
    } catch {
      toast.error("Transmission failed — complete every field and retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-8 lg:gap-12 mb-16">
          <div ref={rocketRef} className="relative shrink-0 hidden sm:block" data-testid="contact-badge">
            <motion.div
              initial={{ y: -220, opacity: 0 }}
              animate={rocketInView ? { y: [-220, 0, -12, 0], opacity: 1 } : {}}
              transition={{ duration: 1.4, times: [0, 0.72, 0.88, 1], ease: "easeOut" }}
              className="relative"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.2 }}
                animate={rocketInView ? { opacity: [0, 0.9, 0], scale: [0.2, 1.6, 1.9] } : {}}
                transition={{ delay: 1, duration: 0.8, ease: "easeOut" }}
                className="absolute inset-0 rounded-full border-2 border-signal"
              />
              <motion.div
                initial={{ opacity: 0, scaleY: 0.2 }}
                animate={rocketInView ? { opacity: [0, 1, 0], scaleY: [0.2, 1.3, 0.3] } : {}}
                transition={{ delay: 0.9, duration: 0.9, ease: "easeOut" }}
                className="absolute left-1/2 -translate-x-1/2 top-full w-5 h-10 origin-top bg-gradient-to-b from-signal via-signal/50 to-transparent blur-[2px]"
                style={{ clipPath: "polygon(50% 100%, 0 0, 100% 0)" }}
              />
              <motion.img
                src="/logo-full.webp"
                width={800}
                height={801}
                loading="lazy"
                alt="Launchpad TV — Launch Where You Land badge"
                className="relative w-32 lg:w-40 h-auto"
                animate={rocketInView ? { y: [0, -8, 0] } : {}}
                transition={{ delay: 1.6, duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal">
              Open Channel
            </span>
            <h2 className="mt-3 font-head font-extrabold tracking-tight text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              Start where you are. Be seen everywhere.
            </h2>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xl" data-testid="contact-copy">
              Join the launch waitlist, book connected TV advertising, apply as a channel partner or
              pitch your show. Every enquiry is read by the Launchpad TV team.
            </p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative glass-panel p-8 lg:p-12 overflow-hidden"
            data-testid="waitlist-panel"
          >
            <div className="absolute -top-10 -right-6 font-display font-extrabold text-[10rem] leading-none text-outline select-none pointer-events-none">
              001
            </div>
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal">
              Launch Alerts
            </span>
            <h3 className="mt-4 font-head font-extrabold tracking-tight text-xl sm:text-2xl text-white">
              Be there when the signal goes live — 11.14.2026.
            </h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              One transmission when we launch. Waitlist members get priority creator
              onboarding and first pick of launch-window sponsorship inventory.
            </p>

            <div className="mt-8 flex items-baseline gap-3">
              <span data-testid="waitlist-counter" className="font-head font-extrabold tabular-nums text-4xl sm:text-5xl text-white">
                {count === null ? "——" : String(count).padStart(4, "0")}
              </span>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
                already on the signal
              </span>
            </div>

            <form onSubmit={joinWaitlist} className="mt-8 flex" data-testid="waitlist-form">
              <input
                data-testid="waitlist-email-input"
                type="email"
                required
                value={wEmail}
                onChange={(e) => setWEmail(e.target.value)}
                placeholder="you@yourchannel.tv"
                className="flex-1 min-w-0 bg-white/5 border border-white/15 border-r-0 px-4 py-3.5 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors"
              />
              <button
                data-testid="waitlist-submit-button"
                type="submit"
                disabled={wLoading}
                className="group flex items-center gap-2 bg-signal text-obsidian font-bold font-mono text-[11px] tracking-[0.2em] uppercase px-6 hover:bg-white hover:text-obsidian transition-colors duration-300 disabled:opacity-50"
              >
                {wLoading ? "..." : "Join"}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </motion.div>

          <motion.form
            onSubmit={sendEnquiry}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="glass-panel p-8 lg:p-12"
            data-testid="enquiry-form"
          >
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal">
              Direct Line
            </span>
            <h3 className="mt-4 font-head font-extrabold tracking-tight text-xl sm:text-2xl text-white">
              Tell us what you're launching.
            </h3>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="enquiry-type-selector">
              {ENQUIRY_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`enquiry-type-${t.id}`}
                  onClick={() => setForm({ ...form, enquiry_type: t.id })}
                  className={`font-mono text-[10px] tracking-[0.15em] uppercase px-3.5 py-2.5 sm:py-2 border transition-colors duration-300 ${
                    form.enquiry_type === t.id
                      ? "border-signal bg-signal/15 text-white"
                      : "border-white/10 text-slate-500 hover:text-white hover:border-white/30"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <input
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className="bg-white/5 border border-white/15 px-4 py-3.5 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors"
              />
              <input
                data-testid="enquiry-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="bg-white/5 border border-white/15 px-4 py-3.5 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors"
              />
            </div>
            <textarea
              data-testid="enquiry-message-input"
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Your show, your brand, your big idea…"
              className="mt-4 w-full bg-white/5 border border-white/15 px-4 py-3.5 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors resize-none"
            />
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={sending}
              className="mt-6 w-full sm:w-auto group flex items-center justify-center gap-3 bg-signal text-obsidian font-bold font-mono text-[11px] tracking-[0.2em] uppercase px-8 py-4 signal-glow hover:bg-white hover:text-obsidian transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Transmitting…" : "Transmit Enquiry"}
              <Send size={13} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
